import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { KeyRound } from 'lucide-react';
import clsx from 'clsx';

interface RotateKeyDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newApiKey: string) => Promise<void>;
  providerName: string;
  loading: boolean;
}

export const RotateKeyDialog: React.FC<RotateKeyDialogProps> = ({
  open,
  onClose,
  onConfirm,
  providerName,
  loading,
}) => {
  const { t } = useTranslation();
  const [newApiKey, setNewApiKey] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setNewApiKey('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!newApiKey.trim()) {
      setError(t('llmConfig.dialog.newApiKeyRequired'));
      return;
    }

    await onConfirm(newApiKey.trim());
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('llmConfig.dialog.rotateKeyTitle')}
      maxWidth='sm'
      showCloseButton={!loading}
      footer={
        <div className='flex justify-end space-x-3'>
          <button
            type='button'
            onClick={onClose}
            disabled={loading}
            className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {t('common.cancel')}
          </button>
          <button
            type='button'
            onClick={handleSubmit}
            disabled={loading || !newApiKey.trim()}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            {loading ? t('llmConfig.dialog.rotating') : t('llmConfig.dialog.rotateConfirm')}
          </button>
        </div>
      }
    >
      <div className='space-y-4'>
        <div className='flex items-center space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
          <KeyRound size={20} className='text-blue-600 flex-shrink-0' />
          <p className='text-sm text-blue-800'>
            {t('llmConfig.dialog.rotateKeyWarning', { name: providerName })}
          </p>
        </div>

        <div>
          <label htmlFor='new_api_key' className='block text-sm font-medium text-gray-700 mb-1'>
            {t('llmConfig.dialog.newApiKeyLabel')} *
          </label>
          <input
            id='new_api_key'
            type='password'
            value={newApiKey}
            onChange={e => {
              setNewApiKey(e.target.value);
              if (error) setError('');
            }}
            disabled={loading}
            placeholder='sk-...'
            autoComplete='off'
            className={clsx(
              'w-full px-3 py-2 border rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'disabled:bg-gray-100 disabled:cursor-not-allowed font-mono text-sm',
              error ? 'border-red-500' : 'border-gray-300'
            )}
          />
          {error && <p className='mt-1 text-sm text-red-600'>{error}</p>}
          <p className='mt-1 text-xs text-gray-500'>{t('llmConfig.dialog.newApiKeyHelp')}</p>
        </div>
      </div>
    </Modal>
  );
};

export default RotateKeyDialog;
